import axios from "../../../lib/http/axiosConfig";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./DroneControl.css";
import { useDispatch, useSelector } from "react-redux";
import {
  clearMissionResult,
  resetMissionProgress,
  setControl,
  setMissionReturning,
  setUploadStatus,
} from "../../../features/DroneSlice";
import { toast } from "react-toastify";
import {
  fetchWaypoints,
  setRouteConfirmed,
  setHoverCellId,
  setSelectedCellIds,
  syncSelectedCellIds,
  setWaypoints,
  updateRouteWaypoints,
} from "../../../features/RouteSlice";
import {
  clearMissionStopRequestedLatch,
  dismissMissionStopReturningToast,
  didMissionStop,
  markMissionStopRequested,
  showMissionEndToastIfNeeded,
  showMissionStopReturningToast,
} from "../../../utils/missionStoppedToast";
import { useDemoScenario } from "../../../demo/DemoScenarioContext";
import { listDemoSections } from "../../../demo/demoSections";
import { writeDemoCellSelection } from "../../../demo/demoCellSelection";
import { isGpsFlightReady, GPS_NEEDS_CHECK } from "../../../utils/gpsStatus";
import { flightOrder } from "../../../utils/flightOrder";
import CalmSelect from "../Datatab/CropMonitoring/CalmSelect";
import { useT } from "../../../i18n";


export default function DroneDetailPanelControl({ powerDisabled = false, batteryPercent = null, sectionsRefresh = 0 }) {
  const t = useT();
  const { isDemo, startDemoMission, stopDemoMission } = useDemoScenario();
  const controls = useSelector((state) => state.drone.control);
  const missionReturning = useSelector((state) => state.drone.missionReturning);
  const upload = useSelector((state) => state.drone.upload);
  const droneId = useSelector((state) => state.drone.selectedDrone);
  const routeId = useSelector((state) => state.route.routeId);
  // [미션 업로드] "드론이 이 농장의 경로를 보관 중" — 업로드 완료 표시와 Start 활성의 근거.
  // 드론이 route_id 를 함께 보고했으면 현재 경로와 일치할 때만 인정한다(다른 농장 경로 보관 중 오인 방지).
  const uploadConfirmed =
    Boolean(upload?.status) &&
    (upload?.routeId == null ||
      routeId == null ||
      Number(upload.routeId) === Number(routeId));
  // [업로드 대기] 서버가 업로드를 접수한 시각. 누른 PC 뿐 아니라 같은 드론을 보고 있는 모든 화면이 받는다.
  const uploadPendingAt = upload?.pendingAt ?? null;
  const selectedCellIds = useSelector((state) => state.route.selectedCellIds);
  const farmId = useSelector((state) => state.farm.farmId);
  const gridCells = useSelector((state) => state.farm.gridCells);
  const dispatch = useDispatch();

  // 비동기 콜백이 최신 controls 를 읽되 매 렌더마다 재생성되지 않도록 ref 로 보관한다.
  const controlsRef = useRef(controls);
  useEffect(() => {
    controlsRef.current = controls;
  }, [controls]);

  const showPopup = (msg) => {
    toast.error(msg);
  };

  const warnToast = (toastId, msg) => toast.warn(msg, { toastId });
  const errorToast = (toastId, msg) => toast.error(msg, { toastId });

  const speakMessage = (message) => {
    if ("speechSynthesis" in window) {
      const utterance = new SpeechSynthesisUtterance(message);
      utterance.lang = "en-US";
      utterance.rate = 0.8;
      utterance.pitch = 1.0;
      speechSynthesis.speak(utterance);
    }
  };

  const [cooldown, setCooldown] = useState(false);
  const cooldownRef = useRef(null);

  // [미션 업로드] 비행 고도(m). 전체 웨이포인트에 일괄 적용, 허용 범위 1~10m.
  const MISSION_ALT_MIN = 1;
  const MISSION_ALT_MAX = 10;
  const MISSION_ALT_DEFAULT = 4;
  // 실드론은 arm 전에 GPS 사전점검(최대 30s) + arm 대기(15s)를 거치므로 여유를 둔다.
  const UPLOAD_TIMEOUT_MS = 60000;
  // 데모가 드론 보고를 흉내 내기까지의 시간. 0 이면 버튼을 누르는 순간 완료 토스트가 떠
  // 업로드가 오가는 과정이 화면에 드러나지 않는다. 실드론(수 초)보다는 짧게 둔다.
  const DEMO_UPLOAD_CONFIRM_MS = 1000;
  // [배터리 게이트 — 하드웨어팀 요청] 이륙 시점 배터리가 이 값 미만이면 Start 를 막는다.
  // 서버(app.mission.min-battery-percent, DRONE-012)와 같은 값. 서버가 최종 판정, 여기는 미리 잠그는 용도.
  const MISSION_MIN_BATTERY_PERCENT = 60;
  const batteryTooLow =
    batteryPercent != null && Number(batteryPercent) < MISSION_MIN_BATTERY_PERCENT;
  const [missionAltitude, setMissionAltitude] = useState(String(MISSION_ALT_DEFAULT));
  // [섹션 불러오기] 저장해 둔 셀 묶음. 섹션 카드에서 바뀌면 sectionsRefresh 로 다시 읽는다.
  const [sections, setSections] = useState([]);
  useEffect(() => {
    if (!farmId) return;
    if (isDemo) {
      setSections(listDemoSections(farmId));
      return undefined;
    }
    let alive = true;
    axios
      .get(`/api/farms/${farmId}/sections`)
      .then(({ data }) => {
        if (alive) setSections(Array.isArray(data?.data) ? data.data : []);
      })
      .catch((error) => console.warn("[SECTION] 목록 조회 실패", error));
    return () => {
      alive = false;
    };
  }, [farmId, sectionsRefresh, isDemo]);
  const [uploading, setUploading] = useState(false);
  const uploadTimeoutRef = useRef(null);
  // 데모의 "드론이 보고할 때까지" 타이머. 취소·언마운트에서 반드시 걷어야 한다.
  const demoUploadTimerRef = useRef(null);

  const parsedAltitude = Number(missionAltitude);
  const altitudeValid =
    missionAltitude !== "" &&
    Number.isFinite(parsedAltitude) &&
    parsedAltitude >= MISSION_ALT_MIN &&
    parsedAltitude <= MISSION_ALT_MAX;

  const clampAltitude = (n) =>
    Math.min(MISSION_ALT_MAX, Math.max(MISSION_ALT_MIN, Math.round(n)));

  const adjustAltitude = (delta) =>
    setMissionAltitude((v) => {
      const cur = Number(v);
      const base = v !== "" && Number.isFinite(cur) ? cur : MISSION_ALT_DEFAULT;
      return String(clampAltitude(base + delta));
    });

  // 직접 입력 후 포커스가 빠질 때 범위(1~10)로 정리한다. 빈 값이면 기본값으로.
  const normalizeAltitude = () => {
    const n = Number(missionAltitude);
    if (missionAltitude === "" || !Number.isFinite(n)) {
      setMissionAltitude(String(MISSION_ALT_DEFAULT));
      return;
    }
    setMissionAltitude(String(clampAltitude(n)));
  };

  useEffect(
    () => () => {
      clearTimeout(cooldownRef.current);
      clearTimeout(uploadTimeoutRef.current);
      clearTimeout(demoUploadTimerRef.current);
    },
    []
  );

  const withCooldown = useCallback(
    (handler) => async () => {
      if (cooldown) return;
      setCooldown(true);
      await handler();
      clearTimeout(cooldownRef.current);
      cooldownRef.current = setTimeout(() => setCooldown(false), 100);
    },
    [cooldown]
  );

  const updatePowerStatus = async () => {
    if (!droneId) {
      warnToast("control-no-drone", t("mission.control.toast.noDrone"));
      return;
    }

    if (controls.missionStatus) {
      warnToast(
        "control-power-requires-stop",
        t("mission.control.toast.powerRequiresStop")
      );
      return;
    }

    if (controls.armingStatus) {
      warnToast(
        "control-power-requires-disarmed",
        t("mission.control.toast.powerRequiresDisarmed")
      );
      return;
    }

    const prev = controls;
    const next = !prev.powerStatus;

    dispatch(setControl({ ...prev, powerStatus: next }));

    if (isDemo) {
      speakMessage(next ? "Power On" : "Power Off");
      return;
    }

    try {
      const { data } = await axios.patch(
        `/api/drones/${droneId}/power-status`,
        {
          powerStatus: next,
        }
      );

      dispatch(setControl(data.data));
      speakMessage(next ? "Power On" : "Power Off");
    } catch (error) {
      console.error("Failed to update power status:", error);
      dispatch(setControl(prev));
      showPopup(t("mission.control.toast.powerUpdateFailed"));
    }
  };

  // [미션 업로드] 업로드 완료 신호는 서버 응답이 아니라 드론의 upload_status 텔레메트리다.
  // (드론이 웨이포인트 저장 성공 시 upload_status=true 보고 → WS UPLOAD_STATUS 로 도착)
  // 여기서 arm 은 하지 않는다 — Start(start_mission) 를 받은 드론이 스스로 arm 한다.
  // 확인이 도착하면 업로드 완료로 확정하고, 타임아웃 내 미도착 시 실패 처리한다.
  useEffect(() => {
    if (!uploading) return;
    if (!uploadConfirmed) return;
    clearTimeout(uploadTimeoutRef.current);
    uploadTimeoutRef.current = null;
    setUploading(false);
    console.log("[MISSION-UPLOAD] upload_status 확인 → 업로드 완료, Start 활성화");
    toast.success(t("mission.control.toast.uploadDone"), {
      toastId: "control-upload-done",
    });
    speakMessage("Uploaded");
  }, [uploading, uploadConfirmed, t]); // eslint-disable-line react-hooks/exhaustive-deps

  // 확인을 못 받고 끝났을 때의 정리. 내가 누른 업로드와 다른 PC 가 누른 업로드가 같은 길을 쓴다.
  const failUploadWait = () => {
    uploadTimeoutRef.current = null;
    setUploading(false);
    console.warn("[MISSION-UPLOAD] 타임아웃: upload_status 확인 미수신");
    errorToast("control-upload-timeout", t("mission.control.toast.uploadTimeout"));
  };

  // 업로드 대기는 이제 서버가 들고 있다. 누르지 않은 PC 와 새로고침한 화면도 같은 대기 표시를 낸다.
  // 이미 지난 대기(타임아웃을 넘긴 접수)는 되살리지 않고, 남은 시간만큼만 기다린다.
  useEffect(() => {
    if (!uploadPendingAt) return;
    const left = UPLOAD_TIMEOUT_MS - (Date.now() - uploadPendingAt);
    if (left <= 0) return;
    setUploading(true);
    clearTimeout(uploadTimeoutRef.current);
    uploadTimeoutRef.current = setTimeout(failUploadWait, left);
  }, [uploadPendingAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // [GPS 잠금 사유] 5(3D fix)·6(DGPS) 만 비행 가능. 값이 없으면(드론이 아직 안 보냄) 따로 알린다.
  // 버튼 아래 상시 문구와 클릭 토스트가 같은 문장을 쓴다. 전원이 꺼져 있으면 마지막 GPS 값이 남아 있으므로
  // 이 사유는 전원 검사 뒤에서만 쓴다. 서버(DRONE-013)가 최종 판정.
  const uploadGpsBlockReason = (() => {
    const gps = controls.gpsStatus;
    if (gps == null || !Number.isInteger(Number(gps))) return t("mission.control.block.gpsUnknown");
    if (!isGpsFlightReady(gps)) return t("mission.control.block.gps", { now: gps });
    return null;
  })();

  const uploadMission = async () => {
    if (!droneId) {
      warnToast("control-no-drone", t("mission.control.toast.noDrone"));
      return;
    }
    if (missionReturning) {
      warnToast("control-upload-returning", t("mission.control.toast.uploadReturning"));
      return;
    }
    if (controls.missionStatus) {
      warnToast("control-upload-requires-stop", t("mission.control.toast.uploadRequiresStop"));
      return;
    }
    if (selectedCellIds.length === 0) {
      warnToast("control-upload-requires-cells", t("mission.control.toast.uploadRequiresCells"));
      return;
    }
    if (!altitudeValid) {
      warnToast(
        "control-upload-altitude",
        t("mission.control.toast.altitudeRange", { min: MISSION_ALT_MIN, max: MISSION_ALT_MAX })
      );
      return;
    }
    if (!controls.powerStatus) {
      warnToast("control-upload-requires-power", t("mission.control.toast.uploadRequiresPower"));
      return;
    }
    // [GPS 게이트] Start 와 같은 기준. 드론이 GPS 불량이면 경로 저장을 거부하는데, 그러면 60s 타임아웃까지
    // 이유 없이 기다리게 되므로 업로드 전에 막는다. 5(3D fix) 는 막지 않고 확인 경고만. 서버(DRONE-013)가 최종 판정.
    if (!isDemo && uploadGpsBlockReason) {
      warnToast("control-upload-requires-gps", uploadGpsBlockReason);
      return;
    }
    if (!isDemo && Number(controls.gpsStatus) === GPS_NEEDS_CHECK) {
      warnToast("control-upload-gps-check", t("mission.control.toast.gpsCheck"));
    }
    if (!routeId) {
      warnToast("control-upload-no-route", t("mission.control.toast.noRoute"));
      return;
    }

    // 새 미션 업로드를 시작하면 직전 완주 결과(진행 링 N/N)는 내린다.
    dispatch(clearMissionResult());

    const cellMap = new Map((gridCells || []).map((cell) => [cell.id, cell]));
    // 화면에 보이는 순서(지도 경로 선, 순번 칩)와 같은 함수로 줄 세운다. 2-opt 도 이 안에서 끝난다.
    const sortedCellIds = flightOrder(selectedCellIds, gridCells);

    const baseWaypoints = sortedCellIds
      .map((id, idx) => {
        const cell = cellMap.get(id);
        const center = cell?.center;
        if (!center) return null;
        return {
          id,
          seq: idx + 1,
          gridCellId: id,
          latitude: center.latitude,
          longitude: center.longitude,
          altitude: parsedAltitude,
        };
      })
      .filter(Boolean);

    if (baseWaypoints.length !== selectedCellIds.length || sortedCellIds.length !== selectedCellIds.length) {
      errorToast("control-build-waypoints-failed", t("mission.control.toast.buildWaypointsFailed"));
      return;
    }

    if (isDemo) {
      // 데모: 드론의 upload_status=true 보고를 즉시 시뮬레이션한다.
      // waypoints 도 실계정과 같은 모양으로 넣는다 — 미션 중 지도의 민트 경로선과 진행 표시가
      // 오직 이 값만 입력으로 쓴다(3_Smartdronepage 의 renderWaypoints → useMarkers.updatePolyline).
      // 비워 두면 비행이 시작되는 순간 계획 경로(흰 선)는 숨겨지는데 이어받을 선이 없어 지도가 빈다.
      dispatch(setWaypoints(baseWaypoints));
      // 실계정과 같은 길을 태운다. uploading=true 로 두고 업로드 확인을 올리면
      // 위의 공용 effect 가 "업로드 완료" 토스트와 음성을 낸다.
      // 여기서 직접 speakMessage 를 부르면 그 effect 와 겹쳐 두 번 말하고,
      // 토스트는 실계정에만 뜨는 채로 남는다.
      setUploading(true);
      // 실드론은 저장을 마친 뒤에야 보고한다. 그 사이를 비워 두어야 "대기 → 완료" 가 보인다.
      clearTimeout(demoUploadTimerRef.current);
      demoUploadTimerRef.current = setTimeout(() => {
        demoUploadTimerRef.current = null;
        dispatch(setUploadStatus({ status: true, routeId: routeId ?? null }));
      }, DEMO_UPLOAD_CONFIRM_MS);
      return;
    }

    console.log(
      `[MISSION-UPLOAD] 요청: 셀 ${JSON.stringify(sortedCellIds)}, 고도 ${parsedAltitude}m, 웨이포인트 ${baseWaypoints.length}개`
    );

    // 이전 업로드의 확인 상태가 남아 있으면 새 확인이 오기 전에 완료로 오판하므로 먼저 비운다.
    dispatch(setUploadStatus({ status: false, routeId: null }));
    setUploading(true);

    try {
      // 순서는 위 flightOrder 에서 이미 확정됐다(지그재그 + 2-opt). 여기서 다시 최적화하면
      // 지도에 그린 흰 경로 선과 드론이 실제로 받는 순서가 달라진다(2026-09-14 실측: 3셀에서 뒤바뀜).
      dispatch(setWaypoints(baseWaypoints));

      try {
        await dispatch(
          updateRouteWaypoints({ routeId, waypoints: baseWaypoints })
        ).unwrap();
      } catch (error) {
        setUploading(false);
        errorToast("control-save-waypoints-failed", t("mission.control.toast.saveWaypointsFailed"));
        return;
      }

      const { data } = await axios.post(`/api/drones/${droneId}/mission-upload`);
      console.log("[MISSION-UPLOAD] 업로드 접수:", data?.data);

      // ARMED 텔레메트리를 기다린다. 낙관적 갱신 금지 — 화면은 드론 보고로만 바뀐다.
      clearTimeout(uploadTimeoutRef.current);
      uploadTimeoutRef.current = setTimeout(failUploadWait, UPLOAD_TIMEOUT_MS);
    } catch (error) {
      setUploading(false);
      console.error("[MISSION-UPLOAD] 업로드 실패:", error?.response?.data ?? error);
      const code = error?.response?.data?.error?.code;
      if (code === "WP-007") {
        errorToast(
          "control-upload-altitude",
          t("mission.control.toast.altitudeRange", { min: MISSION_ALT_MIN, max: MISSION_ALT_MAX })
        );
        return;
      }
      // DRONE-013 = 서버 기준 GPS 미충족. 화면의 GPS 는 5s 폴링이라 서버보다 늦을 수 있으니 재동기화.
      if (code === "DRONE-013") {
        warnToast("control-upload-requires-gps", t("mission.control.toast.gpsNotReady"));
        resyncControlsFromServer();
        return;
      }
      errorToast("control-upload-failed", t("mission.control.toast.uploadFailed"));
    }
  };

  // [업로드 취소] 업로드 확인 상태를 무효화한다(다시 업로드해야 Start 가능).
  // 드론은 취소를 모르고 계속 업로드 완료를 보고하므로 서버에 취소를 남긴다. 화면에서만 내리면
  // 다른 PC 에는 완료로 남고, 새로고침에 되살아나고, 서버는 그 경로로 Start 를 허용한다.
  // 드론이 arm 돼 있으면 disarm 도 함께 보낸다(기존 arming-status API 재사용).
  const cancelMissionUpload = async () => {
    if (!droneId) {
      warnToast("control-no-drone", t("mission.control.toast.noDrone"));
      return;
    }
    if (controls.missionStatus) {
      warnToast("control-upload-requires-stop", t("mission.control.toast.cancelRequiresStop"));
      return;
    }
    if (missionReturning) {
      warnToast("control-upload-returning", t("mission.control.toast.uploadReturning"));
      return;
    }

    dispatch(setUploadStatus({ status: false, routeId: null }));

    if (isDemo) {
      // 확정이 예약된 채로 취소하면 1초 뒤에 "업로드 완료" 가 뒤늦게 떠서
      // 취소한 화면이 다시 완료로 돌아간다.
      clearTimeout(demoUploadTimerRef.current);
      demoUploadTimerRef.current = null;
      setUploading(false);
      dispatch(setControl({ ...controls, armingStatus: false }));
      speakMessage("Cancelled");
      return;
    }

    try {
      // 서버에도 취소를 알린다. 예전엔 화면만 비워서, 셀을 고를 때마다 도는 미션 탭의 스냅샷 복원이
      // Redis 에 남은 "업로드 완료" 를 되살렸다. 서버는 마커를 세워 드론의 true 보고를 다음 업로드까지 무시한다.
      await axios.post(`/api/drones/${droneId}/mission-upload/cancel`);
      if (!controls.armingStatus) {
        console.log("[MISSION-UPLOAD] 업로드 취소 요청 완료(드론 disarm 상태)");
        speakMessage("Cancelled");
        return;
      }
      const { data } = await axios.patch(`/api/drones/${droneId}/arming-status`, {
        armingStatus: false,
      });
      dispatch(setControl(data.data));
      console.log("[MISSION-UPLOAD] 업로드 취소(disarm) 요청 완료");
      speakMessage("Disarmed");
    } catch (error) {
      console.error("Failed to cancel mission upload:", error);
      showPopup(t("mission.control.toast.cancelUploadFailed"));
    }
  };

  // 서버가 DRONE-007 로 거절했다는 건 FE 가 들고 있는 ARM 상태가 실제와 다르다는 뜻이다.
  // 서버 값으로 강제 재동기화해서 사용자가 같은 실패를 반복하지 않게 한다.
  const resyncControlsFromServer = useCallback(async () => {
    if (!droneId) return;
    try {
      const { data } = await axios.get(`/api/dashboard/${droneId}/mission`);
      const serverControls = data?.data?.controls;
      if (!serverControls) return;
      dispatch(
        setControl({
          ...controlsRef.current,
          powerStatus:
            serverControls.powerStatus ?? controlsRef.current?.powerStatus,
          armingStatus:
            serverControls.armingStatus ?? controlsRef.current?.armingStatus,
          missionStatus:
            serverControls.missionStatus ?? controlsRef.current?.missionStatus,
          gpsStatus: serverControls.gpsStatus ?? controlsRef.current?.gpsStatus,
        })
      );
      if (typeof serverControls.uploadStatus === "boolean") {
        dispatch(
          setUploadStatus({
            status: serverControls.uploadStatus,
            routeId: serverControls.uploadRouteId ?? null,
          })
        );
      }
    } catch (error) {
      console.error("Failed to resync drone controls:", error);
    }
  }, [droneId, dispatch]);

  const updateMissionStatus = async () => {
    if (!droneId) {
      warnToast("control-no-drone", t("mission.control.toast.noDrone"));
      return;
    }

    const prev = controls;
    const next = !prev.missionStatus;

    if (next) {
      if (!controls.powerStatus) {
        warnToast("control-start-requires-power", t("mission.control.toast.startRequiresPower"));
        return;
      }

      if (batteryTooLow) {
        warnToast(
          "control-start-requires-battery",
          t("mission.control.toast.startRequiresBattery", {
            min: MISSION_MIN_BATTERY_PERCENT,
            now: batteryPercent,
          })
        );
        return;
      }

      // [GPS 게이트] 5(3D fix)/6(DGPS) 만 비행 가능. 5 는 막지 않고 확인 경고만
      // 띄운다. 서버(DRONE-013)가 최종 판정.
      const gps = controls.gpsStatus;
      if (!isGpsFlightReady(gps)) {
        warnToast(
          "control-start-requires-gps",
          t("mission.control.toast.gpsNotReady")
        );
        return;
      }
      if (Number(gps) === GPS_NEEDS_CHECK) {
        warnToast("control-start-gps-check", t("mission.control.toast.gpsCheck"));
      }

      if (!uploadConfirmed) {
        warnToast(
          "control-start-requires-upload",
          t("mission.control.toast.startRequiresUpload")
        );
        return;
      }

      if ((!isDemo && !routeId) || selectedCellIds.length === 0) {
        warnToast(
          "control-start-requires-waypoints",
          t("mission.control.toast.startRequiresWaypoints")
        );
        return;
      }
    }

    if (!next) {
      // stop 이후 재시작은 새 업로드부터 — 드론도 stop/disarm 에서 upload_status 를 내린다.
      dispatch(setUploadStatus({ status: false, routeId: null }));
      if (isDemo) {
        stopDemoMission();
        speakMessage("stop");
        return;
      }
      dispatch(setControl({ ...prev, missionStatus: next }));
      markMissionStopRequested();
      // [복귀 잠금] 공중에 있을 때만 잠근다. 잠금 해제는 고정 타이머가 아니라 드론의 실제 착륙
      // (armed → disarmed 전이, useDroneSocket)에서 한다. 예전 10s 타이머는 실기체 복귀가 그보다 길면
      // 비행 중에 조작이 풀렸다. 이미 disarm 상태(이륙 전 GPS 점검 중 등)면 전이가 올 수 없으니 잠그지 않는다.
      if (prev.armingStatus === true) {
        showMissionStopReturningToast();
        dispatch(setMissionReturning(true));
      }
      dispatch(setWaypoints([]));
      // 선택은 서버가 미션 종료를 확정할 때 한 번만 비운다. 화면마다 보내면 늦게 도착한 지우기가 새 선택을 지운다.
      dispatch(setRouteConfirmed(false));
    }

    if (isDemo) {
      dispatch(setUploadStatus({ status: false, routeId: null }));
      // 지도의 경로선·웨이포인트와 같은 함수로 줄 세운다. 선택한 순서 그대로 날리면
      // 선은 지그재그인데 드론은 다른 길로 가는 화면이 된다.
      startDemoMission(flightOrder(selectedCellIds, gridCells));
      speakMessage("start");
      return;
    }

      try {
        if (next) {
          if (!routeId || selectedCellIds.length === 0) {
            dispatch(setControl(prev));
            warnToast(
              "control-start-requires-waypoints",
              t("mission.control.toast.startRequiresUploadedWaypoints")
            );
            return;
          }

          // [미션 업로드 분리] 웨이포인트 생성/저장/드론 전송은 uploadMission()에서 이미 끝났다.
          // start 는 start_mission 명령만 요청한다.
          console.log("[MISSION-UPLOAD] Start 요청: droneId", droneId);
          dispatch(resetMissionProgress(selectedCellIds.length));
          dispatch(setControl({ ...prev, missionStatus: next }));
        }

      const { data } = await axios.patch(
        `/api/drones/${droneId}/mission-status`,
        {
          missionStatus: next,
        }
      );

        if (next) {
          dispatch(setControl(data.data));
          // 경로는 start_mission 으로 소비됐다. 드론도 upload_status 를 내리므로 화면을 먼저 맞춘다.
          dispatch(setUploadStatus({ status: false, routeId: null }));
          showMissionEndToastIfNeeded(
            prev.missionStatus,
            data.data?.missionStatus
          );
        } else {
          dispatch(setControl(data.data));
        }
        if (didMissionStop(prev.missionStatus, data.data?.missionStatus)) {
          dispatch(setWaypoints([]));
          // 선택은 서버가 미션 종료를 확정할 때 한 번만 비운다. 화면마다 보내면 늦게 도착한 지우기가 새 선택을 지운다.
          dispatch(setRouteConfirmed(false));
        }
        if (next && farmId) {
          const res = await dispatch(
            fetchWaypoints({ farmId, allowSelectionOverwrite: true })
          ).unwrap();
          const orderedIds = Array.isArray(res?.waypoints)
            ? res.waypoints
                .map((w) => w.gridCellId ?? w.gridCell ?? w.id)
                .filter(Boolean)
            : [];
          if (orderedIds.length > 0) {
            dispatch(syncSelectedCellIds(orderedIds));
          }
        }
        speakMessage(next ? "start" : "stop");
    } catch (error) {
      console.error("Failed to update mission status:", error);
      dispatch(setControl(prev));
      if (!next) {
        clearMissionStopRequestedLatch();
        dismissMissionStopReturningToast();
        dispatch(setMissionReturning(false));
      }

      // DRONE-012 = 서버 기준 배터리 부족(하드웨어팀 요청 임계값). 텔레메트리와 화면이 어긋났을 수 있으니 재동기화.
      if (error?.response?.data?.error?.code === "DRONE-012") {
        await resyncControlsFromServer();
        errorToast(
          "control-mission-status-battery-low",
          t("mission.control.toast.batteryLowServer", { min: MISSION_MIN_BATTERY_PERCENT })
        );
        return;
      }

      // DRONE-011 = 서버가 드론의 upload_status=true 보고를 못 봤다(확인 안 된 경로로는 start 불가).
      // 화면의 업로드 확인을 내려 사용자가 다시 업로드하게 한다.
      if (error?.response?.data?.error?.code === "DRONE-011") {
        dispatch(setUploadStatus({ status: false, routeId: null }));
        await resyncControlsFromServer();
        errorToast(
          "control-mission-status-not-uploaded",
          t("mission.control.toast.notUploadedServer")
        );
        return;
      }

      // DRONE-013 = 서버 기준 GPS 미충족. 텔레메트리와 화면이 어긋났을 수 있으니 재동기화.
      if (error?.response?.data?.error?.code === "DRONE-013") {
        await resyncControlsFromServer();
        errorToast(
          "control-mission-status-gps-not-ready",
          t("mission.control.toast.gpsNotReady")
        );
        return;
      }

      // DRONE-007 = 서버가 보는 ARM 상태와 화면이 어긋났다는 신호.
      // prev 로 되돌리면 여전히 ARMED 라 사용자가 같은 실패를 반복하게 되므로
      // 서버 값으로 강제 재동기화한다.
      if (error?.response?.data?.error?.code === "DRONE-007") {
        await resyncControlsFromServer();
        errorToast(
          "control-mission-status-not-armed",
          t("mission.control.toast.notArmed")
        );
        return;
      }

      errorToast("control-mission-status-failed", t("mission.control.toast.missionStatusFailed"));
    }
  };

  // [미션 업로드] 버튼이 비활성인 이유를 우선순위대로 하나만 보여준다.
  // 순서: 계획(셀 → 고도) 먼저, 전원은 업로드 직전 마지막 관문.
  const uploadBlockReason = (() => {
    if (controls.missionStatus || uploading || uploadConfirmed) return null;
    if (missionReturning) return t("mission.control.block.returning");
    if (selectedCellIds.length === 0) return t("mission.control.block.noCells");
    if (!altitudeValid) return t("mission.control.block.altitude", { min: MISSION_ALT_MIN, max: MISSION_ALT_MAX });
    if (!controls.powerStatus) return t("mission.control.block.power");
    // 배터리 0 은 측정 전 과도 구간일 수 있다(Mission.jsx 참고). 잠그지는 않되 원인은 알려 준다.
    // 잠그지 않으므로 업로드 버튼은 살아 있고, 이 문구는 상태 줄에만 뜬다.
    if (!isDemo && uploadGpsBlockReason) return uploadGpsBlockReason;
    return null;
  })();
  const batteryReadingPending = !isDemo && controls.powerStatus && batteryPercent === 0;
  const uploadReady = !uploadBlockReason && !uploading && !uploadConfirmed && !controls.missionStatus;
  // 업로드 완료(드론 upload_status 확인) 후 Start 를 누를 수 있는 상태 — 버튼 강조(ready)와 상태 문구에 사용
  // [Start 잠금 사유] 비행 중이 아닐 때 Start 를 못 누르는 이유를 우선순위대로 하나만 보여준다.
  // 순서: 복귀 중 → 전원 → 배터리(하드웨어팀 기준) → 업로드 확인. 서버 게이트(DRONE-006/012/011)와 같은 순서.
  const startBlockReason = (() => {
    if (controls.missionStatus || uploading) return null;
    if (missionReturning) return t("mission.control.block.returning");
    if (!controls.powerStatus || powerDisabled) return t("mission.control.block.power");
    if (batteryReadingPending) return t("mission.control.block.batteryPending");
    if (batteryTooLow) return t("mission.control.block.battery", { now: batteryPercent, min: MISSION_MIN_BATTERY_PERCENT });
    if (!uploadConfirmed) return t("mission.control.block.upload");
    return null;
  })();
  const startReady =
    !controls.missionStatus &&
    uploadConfirmed &&
    !batteryTooLow &&
    controls.powerStatus &&
    !powerDisabled &&
    !missionReturning &&
    !uploading;

  // 비행 순서. 업로드와 지도 경로 선이 같은 함수를 쓴다.
  const orderedCellIds = useMemo(() => flightOrder(selectedCellIds, gridCells), [selectedCellIds, gridCells]);
  const hoverCellId = useSelector((state) => state.route.hoverCellId);

  /** 섹션의 셀을 공유 선택으로 채우고 고도를 맞춘다. 이후 업로드는 평소와 같다. */
  const loadSection = async (id) => {
    const section = sections.find((x) => String(x.id) === String(id));
    if (!section) return;
    if (controls.missionStatus || missionReturning || uploading || uploadConfirmed) {
      warnToast(
        "control-load-busy",
        uploadConfirmed ? t("mission.control.toast.loadBusyCancelFirst") : t("mission.control.toast.loadBusy")
      );
      return;
    }
    dispatch(setSelectedCellIds(section.cellIds));
    if (section.altitude != null) setMissionAltitude(String(section.altitude));
    if (isDemo) {
      // 데모의 선택은 서버가 아니라 브라우저에 있다. 여기서 안 써 주면 새로고침에 사라진다.
      dispatch(setSelectedCellIds(writeDemoCellSelection(farmId, section.cellIds)));
      return;
    }
    try {
      const { data } = await axios.put(`/api/farms/${farmId}/cell-selection`, { cellIds: section.cellIds });
      dispatch(setSelectedCellIds(data?.data?.cellIds ?? section.cellIds));
    } catch (error) {
      errorToast("control-load-failed", t("mission.control.toast.loadFailed"));
    }
  };

  // 지금 선택이 어느 섹션과 같은지(집합 비교). 같은 게 없으면 직접 선택.
  const matchedSection = (() => {
    if (selectedCellIds.length === 0) return null;
    const set = new Set(selectedCellIds);
    return sections.find((x) => x.cellIds.length === set.size && x.cellIds.every((c) => set.has(c))) ?? null;
  })();
  const flightZone =
    selectedCellIds.length === 0
      ? t("mission.control.zone.none")
      : t("mission.control.zone.summary", {
          name: matchedSection ? matchedSection.name : t("mission.control.zone.manual"),
          count: selectedCellIds.length,
        });

  const phase = controls.missionStatus
    ? { text: t("mission.control.phase.flying"), cls: "busy" }
    : missionReturning
      ? { text: t("mission.control.phase.returning"), cls: "warn" }
      : uploading
        ? { text: t("mission.control.phase.uploading"), cls: "busy" }
        : uploadConfirmed
          ? { text: t("mission.control.phase.ready"), cls: "ok" }
          : !controls.powerStatus
            ? { text: t("mission.control.phase.powerOff"), cls: "" }
            : { text: t("mission.control.phase.idle"), cls: "" };
  const phaseHelp = controls.missionStatus
    ? t("mission.control.help.flying")
    : uploading
      ? t("mission.control.help.uploading")
      : uploadConfirmed
        ? t("mission.control.help.ready")
        : t("mission.control.help.idle");
  const blockReason = !controls.missionStatus ? (uploadBlockReason || startBlockReason) : null;
  const altitudeLocked = uploading || uploadConfirmed || controls.missionStatus || missionReturning;

  return (
    <>
    <section className="sb-card mission-card">
      <div className="mc-head">
        <div className="sb-title-with-help">
          <h3 className="drone_control_title">{t("mission.control.title")}</h3>
          <button
            type="button"
            className="sb-help"
            aria-label={t("mission.control.helpAria")}
            data-tip={t("mission.control.help.card")}
          >
            ?
          </button>
        </div>
        <span className={`mc-chip ${phase.cls}`}>{phase.text}</span>
      </div>

      <div className="mc-box">
        <div className="mc-row">
          <span className="mc-k">{t("mission.control.power")}</span>
          <span className="mc-v inline">
            <span className={controls.powerStatus ? "mc-on" : "mc-off"}>{controls.powerStatus ? "ON" : "OFF"}</span>
            <label
              className="scheduler-switch"
              title={controls.powerStatus ? t("mission.control.powerOff") : t("mission.control.powerOn")}
            >
              <input
                type="checkbox"
                checked={Boolean(controls.powerStatus)}
                disabled={controls.missionStatus || missionReturning || powerDisabled || cooldown}
                aria-label={controls.powerStatus ? t("mission.control.powerOff") : t("mission.control.powerOn")}
                onChange={withCooldown(updatePowerStatus)}
              />
              <span className="scheduler-slider" />
            </label>
          </span>
        </div>
        <div className="mc-row">
          <span className="mc-k">{t("mission.control.zone")}</span>
          <span className="mc-v">{flightZone}</span>
        </div>
        {orderedCellIds.length > 0 && (
          <div className="mc-strip" aria-label={t("mission.control.order")} onMouseLeave={() => dispatch(setHoverCellId(null))}>
            {orderedCellIds.map((id, i) => (
              <span
                className={`mc-wp${hoverCellId === id ? " hot" : ""}`}
                key={id}
                title={t("mission.control.order.cell", { n: i + 1 })}
                onMouseEnter={() => dispatch(setHoverCellId(id))}
              >
                {i + 1}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="mc-load">
        <CalmSelect
          ariaLabel={t("mission.control.section.load")}
          value=""
          placeholder={sections.length ? t("mission.control.section.placeholder") : t("mission.control.section.empty")}
          disabled={altitudeLocked || sections.length === 0}
          options={sections.map((x) => ({
            value: String(x.id),
            label: t("mission.control.section.option", {
              name: x.name,
              count: x.cellIds.length,
              altitude: x.altitude,
            }),
          }))}
          onChange={(id) => loadSection(id)}
        />
        <div className={`altitude-stepper mc-alt${altitudeLocked ? " disabled" : ""}`} title={t("mission.control.altitude.title")}>
          <button
            type="button"
            className="altitude-step"
            aria-label={t("mission.control.altitude.decrease")}
            disabled={altitudeLocked || parsedAltitude <= MISSION_ALT_MIN}
            onClick={() => adjustAltitude(-1)}
          >
            −
          </button>
          <input
            className={`altitude-value-input${!altitudeValid ? " invalid" : ""}`}
            type="number"
            min={MISSION_ALT_MIN}
            max={MISSION_ALT_MAX}
            step={1}
            value={missionAltitude}
            aria-label={t("mission.control.altitude.input")}
            disabled={altitudeLocked}
            onChange={(e) => setMissionAltitude(e.target.value)}
            onBlur={normalizeAltitude}
          />
          <span className="altitude-unit">m</span>
          <button
            type="button"
            className="altitude-step"
            aria-label={t("mission.control.altitude.increase")}
            disabled={altitudeLocked || parsedAltitude >= MISSION_ALT_MAX}
            onClick={() => adjustAltitude(1)}
          >
            +
          </button>
        </div>
      </div>

      <div className="mc-ctl">
        <button
          type="button"
          className={`mc-btn${uploadConfirmed ? " done" : ""}${cooldown ? " cooldown" : ""}`}
          aria-label={uploadConfirmed ? t("mission.control.upload.cancelAria") : t("mission.control.upload.aria")}
          aria-pressed={uploadConfirmed}
          disabled={powerDisabled || missionReturning || uploading || controls.missionStatus || (!uploadReady && !uploadConfirmed)}
          onClick={withCooldown(uploadConfirmed ? cancelMissionUpload : uploadMission)}
        >
          {uploading
            ? t("mission.control.upload.confirming")
            : uploadConfirmed
              ? t("mission.control.upload.confirmedCancel")
              : t("mission.control.upload.label")}
        </button>
        <button
          type="button"
          className={`mc-btn ${controls.missionStatus ? "stop" : "pri"}${startReady ? " ready" : ""}${cooldown ? " cooldown" : ""}`}
          aria-label={controls.missionStatus ? t("mission.control.stop") : t("mission.control.start")}
          aria-pressed={controls.missionStatus}
          disabled={powerDisabled || missionReturning || uploading || (!controls.missionStatus && !startReady)}
          onClick={withCooldown(updateMissionStatus)}
        >
          {controls.missionStatus ? t("mission.control.stop") : t("mission.control.start")}
        </button>
      </div>
      <div className={`mc-help${blockReason ? " block" : ""}`}>{blockReason || phaseHelp}</div>
    </section>
    </>
  );
}
